import React, { useState, useEffect } from 'react';
import StudioBanner from '../../components/studio/StudioBanner';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useParams, useNavigate } from 'react-router-dom';
import { studioAdminApi as adminApi } from '../../api/studioClient';
import studioApi from '../../api/studioClient';
const api = studioApi;
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X, GripVertical, Eye, EyeOff } from 'lucide-react';
import AdminPageHeader from '../../components/studio/AdminPageHeader';

const FIELD_TYPES = [
  { value: 'TEXT', label: 'Text', desc: 'Single line text' },
  { value: 'TEXTAREA', label: 'Text Area', desc: 'Multi-line text' },
  { value: 'NUMBER', label: 'Number', desc: 'Integer or decimal' },
  { value: 'CURRENCY', label: 'Currency', desc: 'Money amount' },
  { value: 'DATE', label: 'Date', desc: 'Date picker' },
  { value: 'DATETIME', label: 'Date & Time', desc: 'Date and time' },
  { value: 'DROPDOWN', label: 'Dropdown', desc: 'Single select from list' },
  { value: 'MULTI_SELECT', label: 'Multi-Select', desc: 'Multiple selections' },
  { value: 'CHECKBOX', label: 'Checkbox', desc: 'Yes / No' },
  { value: 'TOGGLE', label: 'Toggle', desc: 'On / Off switch' },
  { value: 'PHONE', label: 'Phone', desc: 'Phone number' },
  { value: 'EMAIL', label: 'Email', desc: 'Email address' },
  { value: 'URL', label: 'URL', desc: 'Web address' },
  { value: 'ADDRESS', label: 'Address', desc: 'Street, city, state, zip' },
  { value: 'FILE', label: 'File Upload', desc: 'Attach files' },
  { value: 'RICH_TEXT', label: 'Rich Text', desc: 'Formatted text editor' },
  { value: 'RECORD_LINK', label: 'Record Link', desc: 'Link to another record type' },
  { value: 'USER_LOOKUP', label: 'User Lookup', desc: 'Assign a team member' },
  { value: 'CALCULATED', label: 'Calculated', desc: 'Auto-computed from formula' },
  { value: 'REPEATER', label: 'Repeater Table', desc: 'Multi-row structured data' },
  { value: 'REPEATER_SECTION', label: 'Repeater Section', desc: 'Collapsible multi-row section with full fields' },
];

const FIELD_TYPE_COLORS = {
  TEXT: 'bg-blue-50 text-blue-700', TEXTAREA: 'bg-blue-50 text-blue-700',
  NUMBER: 'bg-green-50 text-green-700', CURRENCY: 'bg-green-50 text-green-700',
  DATE: 'bg-purple-50 text-purple-700', DATETIME: 'bg-purple-50 text-purple-700',
  DROPDOWN: 'bg-orange-50 text-orange-700', MULTI_SELECT: 'bg-orange-50 text-orange-700',
  CHECKBOX: 'bg-pink-50 text-pink-700', TOGGLE: 'bg-pink-50 text-pink-700',
  PHONE: 'bg-teal-50 text-teal-700', EMAIL: 'bg-teal-50 text-teal-700',
  URL: 'bg-teal-50 text-teal-700', ADDRESS: 'bg-yellow-50 text-yellow-700',
  FILE: 'bg-gray-50 text-gray-700', RICH_TEXT: 'bg-indigo-50 text-indigo-700',
  RECORD_LINK: 'bg-cyan-50 text-cyan-700',
  USER_LOOKUP: 'bg-violet-50 text-violet-700',
  CALCULATED: 'bg-amber-50 text-amber-700',
  REPEATER: 'bg-rose-50 text-rose-700',
  REPEATER_SECTION: 'bg-fuchsia-50 text-fuchsia-700',
};

function SortableField({ field, onEdit, onDelete, typeColors }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1, zIndex: isDragging ? 10 : 0 };
  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 bg-white">
      <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-gray-300 hover:text-gray-500">
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">{field.label}</span>
          {field.is_required && <span className="text-red-500 text-xs">*required</span>}
          {field.is_visible_in_list && (
            <span className="text-xs text-gray-400 flex items-center gap-0.5"><Eye className="h-3 w-3" /> list</span>
          )}
        </div>
        <p className="text-xs text-gray-400">{field.name}</p>
      </div>
      <span className={`text-xs px-2 py-1 rounded-full font-medium ${typeColors[field.field_type] || 'bg-gray-50 text-gray-600'}`}>
        {field.field_type}
      </span>
      <div className="flex items-center gap-1">
        <button onClick={() => onEdit(field)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button onClick={() => onDelete(field)} className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}


function FormulaInput({ value, onChange, fields, currentFieldId }) {
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [filter, setFilter] = React.useState('');
  const [cursorPos, setCursorPos] = React.useState(0);
  const textareaRef = React.useRef(null);

  const numericFields = (fields || []).filter(f =>
    f.id !== currentFieldId && ['NUMBER', 'CURRENCY', 'CALCULATED'].includes(f.field_type)
  );

  const filtered = numericFields.filter(f =>
    !filter || f.name.toLowerCase().includes(filter.toLowerCase()) || f.label.toLowerCase().includes(filter.toLowerCase())
  );

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    onChange(val);
    setCursorPos(pos);

    const before = val.slice(0, pos);
    const openBrace = before.lastIndexOf('{');
    const closeBrace = before.lastIndexOf('}');
    if (openBrace > closeBrace) {
      setFilter(before.slice(openBrace + 1));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setFilter('');
    }
  };

  const insertField = (fieldName) => {
    const val = value || '';
    const before = val.slice(0, cursorPos);
    const openBrace = before.lastIndexOf('{');
    const after = val.slice(cursorPos);
    const closeBraceAfter = after.indexOf('}');
    const afterClean = closeBraceAfter >= 0 ? after.slice(closeBraceAfter + 1) : after;
    const newVal = before.slice(0, openBrace) + '{' + fieldName + '}' + afterClean;
    onChange(newVal);
    setShowSuggestions(false);
    setFilter('');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  return (
    <div className="relative">
      <textarea ref={textareaRef} value={value || ''} onChange={handleChange}
        onKeyDown={e => {
          if (e.key === 'Escape') setShowSuggestions(false);
          if (e.key === '{') { setShowSuggestions(true); setFilter(''); }
        }}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        rows={3} placeholder="e.g., {policy_limit} - {amount_paid}"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
      {showSuggestions && filtered.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase border-b">Available Fields</div>
          {filtered.map(f => (
            <button key={f.id} type="button"
              onMouseDown={e => { e.preventDefault(); insertField(f.name); }}
              className="w-full text-left px-3 py-2 hover:bg-brand-50 flex items-center justify-between group">
              <div>
                <span className="text-sm font-medium text-gray-900">{f.label}</span>
                <span className="text-xs text-gray-400 ml-2 font-mono">{'{' + f.name + '}'}</span>
              </div>
              <span className="text-[10px] text-gray-300 group-hover:text-brand-500">{f.field_type}</span>
            </button>
          ))}
        </div>
      )}
      {showSuggestions && filtered.length === 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="text-xs text-gray-400">No matching numeric fields found. Type {'{'} to browse all.</p>
        </div>
      )}
    </div>
  );
}

export default function FieldManager() {
  const { recordTypeId } = useParams();
  const navigate = useNavigate();
  const [recordType, setRecordType] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    const reordered = arrayMove(fields, oldIndex, newIndex);
    setFields(reordered);
    try {
      await adminApi.reorderFields(reordered.map((f, i) => ({ id: f.id, sort_order: i })));
    } catch (err) { toast.error('Failed to save order'); }
  };
  const [allRecordTypes, setAllRecordTypes] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    label: '', field_type: '', is_required: false,
    is_visible_in_list: false, help_text: '', config: {}
  });
  const [optionsText, setOptionsText] = useState('');
  const [restorePrompt, setRestorePrompt] = useState(null);
  const [deletePrompt, setDeletePrompt] = useState(null);
  const [checkingImpact, setCheckingImpact] = useState(null);

  const fetchData = async () => {
    try {
      const [rtRes, fieldsRes] = await Promise.all([
        adminApi.getRecordType(recordTypeId),
        adminApi.getFields(recordTypeId),
      ]);
      setRecordType(rtRes.data.record_type);
      setFields(fieldsRes.data.fields);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [recordTypeId]);

  const handleRestoreChoice = async (choice) => {
    if (!restorePrompt) return;
    const { deleted, payload } = restorePrompt;
    setRestorePrompt(null);

    if (choice === 'restore') {
      try {
        await adminApi.restoreField(deleted.id);
        toast.success('Field restored');
        setShowModal(false);
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Restore failed');
      }
    } else if (choice === 'create_new') {
      try {
        await adminApi.createField({ ...payload, skip_restore_check: true });
        toast.success('Field created');
        setForm({ label: '', field_type: '', is_required: false, is_visible_in_list: false, help_text: '', config: {} });
        setOptionsText('');
        fetchData();
      } catch (err) {
        toast.error(err.response?.data?.error || 'Create failed');
      }
    }
    // choice === 'cancel' does nothing — just closes the prompt
  };

  useEffect(() => {
    adminApi.getRecordTypes().then(r => setAllRecordTypes(r.data.record_types || [])).catch(() => {});
    api.get('/roles').then(r => setAllRoles(r.data.roles || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ label: '', field_type: '', is_required: false, is_visible_in_list: false, help_text: '', config: {} });
    setOptionsText('');
    setShowModal(true);
  };

  const openEdit = (field) => {
    setEditing(field);
    setForm({
      label: field.label,
      field_type: field.field_type,
      is_required: field.is_required,
      is_visible_in_list: field.is_visible_in_list,
      help_text: field.help_text || '',
      config: field.config || {},
    });
    setOptionsText(field.config?.options?.join('\n') || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Label is required'); return; }
    if (!form.field_type) { toast.error('Please select a field type'); return; }
    if (form.field_type === 'RECORD_LINK' && !form.config?.target_record_type_id) { toast.error('Target Record Type is required'); return; }
    if (form.field_type === 'REPEATER') {
      const cols = form.config?.columns || [];
      if (cols.length === 0) { toast.error('At least one column is required for repeater fields'); return; }
      if (cols.length > 4) { toast.error('Maximum of 4 columns allowed for repeater fields'); return; }
    }
    if (form.field_type === 'CALCULATED') {
      const calcType = form.config?.calculation_type || 'math';
      if (calcType === 'math' && !form.config?.formula?.trim()) { toast.error('Calculation formula is required'); return; }
      if (calcType === 'date_offset' && !form.config?.source_field) { toast.error('Source date field is required'); return; }
      if (calcType === 'date_offset' && !form.config?.offset_amount) { toast.error('Offset amount is required'); return; }
      if (calcType === 'date_diff' && !form.config?.start_date_field) { toast.error('Start date field is required'); return; }
    }

    const payload = { ...form, record_type_id: recordTypeId };

    // Parse options for dropdown/multi-select
    if (['DROPDOWN', 'MULTI_SELECT'].includes(form.field_type) && optionsText.trim()) {
      payload.config = { ...payload.config, options: optionsText.split(/[,\n\-]+/).map(o => o.trim()).filter(Boolean) };
    }

    try {
      if (editing) {
        await adminApi.updateField(editing.id, payload);
        toast.success('Field updated');
        setShowModal(false);
      } else {
        await adminApi.createField(payload);
        toast.success('Field created — add another or close');
        setForm({ label: '', field_type: '', is_required: false, is_visible_in_list: false, help_text: '', config: {} });
        setOptionsText('');
      }
      fetchData();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.can_restore) {
        setRestorePrompt({
          deleted: err.response.data.deleted_field,
          payload,
        });
      } else {
        toast.error(err.response?.data?.error || 'Save failed');
      }
    }

  };
  const handleDelete = async (field) => {
    setCheckingImpact(field.id);
    try {
      const res = await api.get(`/admin/fields/${field.id}/impact`);
      setDeletePrompt(res.data);
    } catch (err) {
      toast.error('Failed to check impact');
    } finally {
      setCheckingImpact(null);
    }
  };

  const confirmDelete = async () => {
    if (!deletePrompt) return;
    try {
      await adminApi.deleteField(deletePrompt.field.id);
      toast.success('Field deleted');
      setDeletePrompt(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    }
  };

  const handleExportFieldData = async (fieldId, fieldName) => {
    try {
      const res = await api.get(`/admin/fields/${fieldId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fieldName}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Data exported');
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const needsOptions = ['DROPDOWN', 'MULTI_SELECT'].includes(form.field_type);
  const needsRecordLink = form.field_type === 'RECORD_LINK';

  if (loading) return <div className="text-center py-12 text-gray-400">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <StudioBanner />
      <AdminPageHeader title={<><span style={{ color: recordType?.color }}>{recordType?.name}</span> — Fields</>} description="Define the data fields for this record type" backTo="/template-studio">
        <button onClick={openCreate}
          className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
          <Plus className="h-4 w-4" /> Add Field
        </button>
      </AdminPageHeader>

      {/* Field List */}
      {fields.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-1">No fields yet</h3>
          <p className="text-sm text-gray-500 mb-4">Add fields to define what data this record type captures.</p>
          <button onClick={openCreate}
            className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
            <Plus className="h-4 w-4" /> Add First Field
          </button>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fields.map(f => f.id)} strategy={verticalListSortingStrategy}>
            <div className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-100">
              {fields.map((field) => (
                <SortableField key={field.id} field={field} onEdit={openEdit} onDelete={handleDelete} typeColors={FIELD_TYPE_COLORS} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">{editing ? 'Edit' : 'New'} Field</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input type="text" value={form.label}
                  onChange={e => setForm({ ...form, label: e.target.value })}
                  placeholder="e.g., Client Name, Date of Loss, Policy Limit"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>

              {/* Field Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                <div className="grid grid-cols-2 gap-2">
                  {FIELD_TYPES.map(ft => (
                    <button key={ft.value} onClick={() => setForm({ ...form, field_type: ft.value })}
                      className={`text-left px-3 py-2 rounded-lg border-2 text-sm transition-colors ${
                        form.field_type === ft.value ? 'border-brand-500 bg-brand-50' : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      <span className="font-medium">{ft.label}</span>
                      <span className="block text-xs text-gray-400">{ft.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Options for dropdown/multi-select */}
              {needsOptions && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Options (one per line)</label>
                  <textarea value={optionsText}
                    onChange={e => setOptionsText(e.target.value)}
                    rows={5} placeholder={"Option 1\nOption 2\nOption 3"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </div>
              )}

              {/* Record Link Config */}
              {needsRecordLink && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Record Type <span className="text-red-500">*</span></label>
                    <select
                      value={form.config?.target_record_type_id || ''}
                      onChange={e => {
                        const rt = allRecordTypes.find(r => r.id === e.target.value);
                        setForm({ ...form, config: { ...form.config, target_record_type_id: e.target.value, target_type_name: rt?.name || '' } });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                      <option value="" disabled>— Select a record type —</option>
                      {allRecordTypes.filter(rt => rt.id !== recordTypeId).map(rt => (
                        <option key={rt.id} value={rt.id}>{rt.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Users will search and link records of this type</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.config?.allow_multiple || false}
                      onChange={e => setForm({ ...form, config: { ...form.config, allow_multiple: e.target.checked } })}
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                    <span className="text-sm text-gray-700">Allow multiple links</span>
                  </label>
                </div>
              )}

              {/* User Lookup Config */}
              {form.field_type === 'USER_LOOKUP' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role Filter (optional)</label>
                    <select
                      value={form.config?.role_filter || ''}
                      onChange={e => setForm({ ...form, config: { ...form.config, role_filter: e.target.value } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                      <option value="">All users (no filter)</option>
                      {allRoles.map(role => (
                        <option key={role.id} value={role.id}>{role.name}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400 mt-1">Only show users with this role</p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.config?.allow_multiple || false}
                      onChange={e => setForm({ ...form, config: { ...form.config, allow_multiple: e.target.checked } })}
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                    <span className="text-sm text-gray-700">Allow multiple users</span>
                  </label>
                </div>
              )}

              {/* Calculated Field Config */}
              {form.field_type === 'CALCULATED' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Calculation Type</label>
                    <select value={form.config?.calculation_type || 'math'}
                      onChange={e => setForm({ ...form, config: { ...form.config, calculation_type: e.target.value } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                      <option value="math">Math Formula</option>
                      <option value="date_offset">Date Offset (e.g., SOL = Date of Loss + 2 years)</option>
                      <option value="date_diff">Date Difference (days between two dates)</option>
                    </select>
                  </div>

                  {(form.config?.calculation_type || 'math') === 'math' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Formula</label>
                        <FormulaInput value={form.config?.formula || ''}
                          onChange={val => setForm({ ...form, config: { ...form.config, formula: val } })}
                          fields={fields} currentFieldId={form.id} />
                        <p className="text-xs text-gray-400 mt-1">Type <strong>{'{'}</strong> to see available fields. Supports +, -, *, /</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Output Format</label>
                        <select value={form.config?.output_format || 'number'}
                          onChange={e => setForm({ ...form, config: { ...form.config, output_format: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                          <option value="number">Number</option>
                          <option value="currency">Currency ($)</option>
                          <option value="percentage">Percentage (%)</option>
                          <option value="text">Text</option>
                        </select>
                      </div>
                    </>
                  )}

                  {form.config?.calculation_type === 'date_offset' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Source Date Field</label>
                        <select value={form.config?.source_field || ''}
                          onChange={e => {
                            const f = fields.find(fd => fd.id === e.target.value);
                            setForm({ ...form, config: { ...form.config, source_field: e.target.value, source_field_name: f?.name || '' } });
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                          <option value="">Select a date field...</option>
                          {fields.filter(f => ['DATE', 'DATETIME'].includes(f.field_type)).map(f => (
                            <option key={f.id} value={f.id}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Offset Amount</label>
                          <input type="number" value={form.config?.offset_amount ?? ''}
                            onChange={e => setForm({ ...form, config: { ...form.config, offset_amount: e.target.value ? parseInt(e.target.value) : '' } })}
                            placeholder="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                          <select value={form.config?.offset_unit || 'years'}
                            onChange={e => setForm({ ...form, config: { ...form.config, offset_unit: e.target.value } })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                            <option value="days">Days</option>
                            <option value="months">Months</option>
                            <option value="years">Years</option>
                          </select>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">Example: Date of Loss + 2 years = Statute of Limitations deadline</p>
                    </>
                  )}

                  {form.config?.calculation_type === 'date_diff' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date Field</label>
                        <select value={form.config?.start_date_field || ''}
                          onChange={e => setForm({ ...form, config: { ...form.config, start_date_field: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                          <option value="">Select a date field...</option>
                          {fields.filter(f => ['DATE', 'DATETIME'].includes(f.field_type)).map(f => (
                            <option key={f.id} value={f.id}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date Field (or Today)</label>
                        <select value={form.config?.end_date_field || '_today'}
                          onChange={e => setForm({ ...form, config: { ...form.config, end_date_field: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                          <option value="_today">Today (current date)</option>
                          {fields.filter(f => ['DATE', 'DATETIME'].includes(f.field_type)).map(f => (
                            <option key={f.id} value={f.id}>{f.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Result In</label>
                        <select value={form.config?.diff_unit || 'days'}
                          onChange={e => setForm({ ...form, config: { ...form.config, diff_unit: e.target.value } })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                          <option value="days">Days</option>
                          <option value="months">Months</option>
                          <option value="years">Years</option>
                        </select>
                      </div>
                      <p className="text-xs text-gray-400">Example: Days since Date of Loss, or Days until SOL deadline</p>
                    </>
                  )}
                </div>
              )}

              {/* Currency Config */}
              {form.field_type === 'CURRENCY' && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Currency Symbol</label>
                    <select value={form.config?.currency_symbol || '$'}
                      onChange={e => setForm({ ...form, config: { ...form.config, currency_symbol: e.target.value } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                      <option value="$">$ (USD)</option>
                      <option value="€">€ (EUR)</option>
                      <option value="£">£ (GBP)</option>
                      <option value="¥">¥ (JPY/CNY)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Decimal Places</label>
                    <select value={form.config?.decimals ?? 2}
                      onChange={e => setForm({ ...form, config: { ...form.config, decimals: parseInt(e.target.value) } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                      <option value={0}>0 (whole numbers)</option>
                      <option value={2}>2 (standard)</option>
                      <option value={4}>4 (precise)</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Number Config */}
              {form.field_type === 'NUMBER' && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Min</label>
                    <input type="number" value={form.config?.min ?? ''}
                      onChange={e => setForm({ ...form, config: { ...form.config, min: e.target.value ? Number(e.target.value) : undefined } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max</label>
                    <input type="number" value={form.config?.max ?? ''}
                      onChange={e => setForm({ ...form, config: { ...form.config, max: e.target.value ? Number(e.target.value) : undefined } })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Step</label>
                    <input type="number" value={form.config?.step ?? ''}
                      onChange={e => setForm({ ...form, config: { ...form.config, step: e.target.value ? Number(e.target.value) : undefined } })}
                      placeholder="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                  </div>
                </div>
              )}

              {/* Repeater Config */}
              {form.field_type === 'REPEATER' && (
                <RepeaterColumnEditor
                  columns={form.config?.columns || []}
                  onChange={(columns) => setForm({ ...form, config: { ...form.config, columns } })}
                  minRows={form.config?.min_rows}
                  maxRows={form.config?.max_rows}
                  onMinChange={(v) => setForm({ ...form, config: { ...form.config, min_rows: v } })}
                  onMaxChange={(v) => setForm({ ...form, config: { ...form.config, max_rows: v } })}
                />
              )}

              {/* Repeater Section Config */}
              {form.field_type === 'REPEATER_SECTION' && (
                <RepeaterSectionEditor
                  subFields={form.config?.sub_fields || []}
                  onChange={(sub_fields) => setForm({ ...form, config: { ...form.config, sub_fields } })}
                  sectionLabel={form.config?.section_label || ''}
                  onSectionLabelChange={(v) => setForm({ ...form, config: { ...form.config, section_label: v } })}
                  maxRows={form.config?.max_rows}
                  onMaxRowsChange={(v) => setForm({ ...form, config: { ...form.config, max_rows: v } })}
                  allRecordTypes={allRecordTypes}
                  recordTypeId={recordTypeId}
                />
              )}

              {/* Help Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Help Text (optional)</label>
                <input type="text" value={form.help_text}
                  onChange={e => setForm({ ...form, help_text: e.target.value })}
                  placeholder="Instructions shown below the field"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>

              {/* Toggles */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_required}
                    onChange={e => setForm({ ...form, is_required: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                  <span className="text-sm text-gray-700">Required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_visible_in_list}
                    onChange={e => setForm({ ...form, is_visible_in_list: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                  <span className="text-sm text-gray-700">Show in list view</span>
                </label>
              </div>
            </div>
            <div className="flex items-center justify-between p-5 border-t border-gray-100 sticky bottom-0 bg-white">
              <button onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50">Close</button>
              <button onClick={handleSave}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">
                {editing ? 'Save Changes' : 'Save & Add Another'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Impact Modal */}
      {deletePrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${deletePrompt.can_delete ? 'bg-red-100' : 'bg-gray-100'}`}>
                  <svg className={`h-5 w-5 ${deletePrompt.can_delete ? 'text-red-600' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Delete "{deletePrompt.field.label}"</h3>
                  <p className="text-xs text-gray-500">{deletePrompt.field.field_type} field</p>
                </div>
              </div>

              <div className="space-y-2 mb-5">
                {/* Screens impact */}
                {deletePrompt.impact.screens.length > 0 && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                    <span className="text-amber-500 mt-0.5">⚠️</span>
                    <div>
                      <p className="text-sm text-amber-800 font-medium">Used on {deletePrompt.impact.screens.length} screen{deletePrompt.impact.screens.length > 1 ? 's' : ''}</p>
                      <p className="text-xs text-amber-600 mt-0.5">{deletePrompt.impact.screens.map(s => s.name).join(', ')}</p>
                      <p className="text-xs text-amber-500 mt-1">Will be automatically removed from layouts</p>
                    </div>
                  </div>
                )}

                {/* Records with data */}
                {deletePrompt.impact.records_with_data > 0 && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
                    <span className="text-amber-500 mt-0.5">📊</span>
                    <div>
                      <p className="text-sm text-amber-800 font-medium">{deletePrompt.impact.records_with_data} record{deletePrompt.impact.records_with_data > 1 ? 's' : ''} have data</p>
                      <p className="text-xs text-amber-500 mt-0.5">Data preserved in recycle bin for 60 days</p>
                      <button
                        onClick={() => handleExportFieldData(deletePrompt.field.id, deletePrompt.field.name)}
                        className="text-xs text-amber-700 underline hover:text-amber-900 mt-1 font-medium">
                        📥 Export data before deleting
                      </button>
                    </div>
                  </div>
                )}

                {/* Calculated field dependencies — BLOCKER */}
                {deletePrompt.impact.dependent_calc_fields.length > 0 && (
                  <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
                    <span className="text-red-500 mt-0.5">🚫</span>
                    <div>
                      <p className="text-sm text-red-800 font-medium">Blocks deletion</p>
                      <p className="text-xs text-red-600 mt-0.5">Referenced by: {deletePrompt.impact.dependent_calc_fields.map(c => c.label).join(', ')}</p>
                      <p className="text-xs text-red-500 mt-1">Remove the calculated field dependency first</p>
                    </div>
                  </div>
                )}

                {/* No impact */}
                {deletePrompt.impact.screens.length === 0 && deletePrompt.impact.records_with_data === 0 && deletePrompt.impact.dependent_calc_fields.length === 0 && (
                  <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                    <span className="text-green-500 mt-0.5">✅</span>
                    <p className="text-sm text-green-800">No impact — this field is not used anywhere</p>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button onClick={() => setDeletePrompt(null)}
                  className="flex-1 px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                {deletePrompt.can_delete ? (
                  <button onClick={confirmDelete}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">
                    Delete Field
                  </button>
                ) : (
                  <button disabled className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed">
                    Cannot Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Prompt Modal */}
      {restorePrompt && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <svg className="h-5 w-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Deleted field found</h3>
                  <p className="text-sm text-gray-500">A previously deleted field matches this name</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 mb-5">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">{restorePrompt.deleted.label}</span>
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{restorePrompt.deleted.field_type}</span>
                </div>
                <p className="text-xs text-gray-400 mt-1">Deleted {new Date(restorePrompt.deleted.deleted_at).toLocaleDateString()}</p>
              </div>
              <div className="space-y-2">
                <button onClick={() => handleRestoreChoice('restore')}
                  className="w-full flex items-center gap-3 px-4 py-3 border-2 border-brand-200 rounded-lg hover:bg-brand-50 transition-colors text-left">
                  <span className="text-lg">♻️</span>
                  <div>
                    <span className="text-sm font-medium text-brand-700">Restore Original</span>
                    <p className="text-xs text-gray-500">Bring back the deleted field with its previous configuration</p>
                  </div>
                </button>
                <button onClick={() => handleRestoreChoice('create_new')}
                  className="w-full flex items-center gap-3 px-4 py-3 border-2 border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-left">
                  <span className="text-lg">✨</span>
                  <div>
                    <span className="text-sm font-medium text-gray-700">Create New</span>
                    <p className="text-xs text-gray-500">Ignore the deleted field and create a fresh one</p>
                  </div>
                </button>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-xl">
              <button onClick={() => handleRestoreChoice('cancel')}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-1">
                Cancel — don't create anything
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OptionsInput({ options, onChange, placeholder }) {
  const [text, setText] = useState((options || []).join(', '));
  useEffect(() => {
    const joined = (options || []).join(', ');
    setText(prev => {
      const parsed = prev.split(/[,\n\-]+/).map(o => o.trim()).filter(Boolean);
      if (JSON.stringify(parsed) !== JSON.stringify(options || [])) return joined;
      return prev;
    });
  }, [options]);
  return (
    <textarea value={text}
      onChange={e => setText(e.target.value)}
      onBlur={() => onChange(text.split(/[,\n\-]+/).map(o => o.trim()).filter(Boolean))}
      rows={2}
      placeholder={placeholder || "Enter options separated by commas or new lines"}
      className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
  );
}

const COLUMN_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'currency', label: 'Currency' },
  { value: 'year', label: 'Year' },
];

function RepeaterColumnEditor({ columns, onChange, minRows, maxRows, onMinChange, onMaxChange }) {
  const addColumn = () => {
    onChange([...columns, { name: '', label: '', type: 'text', width: 'auto', options: [] }]);
  };
  const updateColumn = (index, updates) => {
    const updated = [...columns];
    updated[index] = { ...updated[index], ...updates };
    // Auto-generate name from label
    if (updates.label) {
      updated[index].name = updates.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
    }
    onChange(updated);
  };
  const removeColumn = (index) => {
    onChange(columns.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">Table Columns</label>
      {columns.length === 0 && (
        <p className="text-xs text-gray-400">No columns defined yet. Add columns to define the row structure.</p>
      )}
      {columns.map((col, i) => (
        <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500">Column {i + 1}</span>
            <button onClick={() => removeColumn(i)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Label</label>
              <input type="text" value={col.label}
                onChange={e => updateColumn(i, { label: e.target.value })}
                placeholder="e.g., Employer Name"
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Type</label>
              <select value={col.type}
                onChange={e => updateColumn(i, { type: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                {COLUMN_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
              </select>
            </div>
          </div>
          {col.type === 'currency' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Symbol</label>
                <select value={col.currency_symbol || '$'}
                  onChange={e => updateColumn(i, { currency_symbol: e.target.value })}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value="$">$ (USD)</option>
                  <option value="€">€ (EUR)</option>
                  <option value="£">£ (GBP)</option>
                  <option value="¥">¥ (JPY/CNY)</option>
                  <option value="₹">₹ (INR)</option>
                  <option value="C$">C$ (CAD)</option>
                  <option value="A$">A$ (AUD)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Decimals</label>
                <select value={col.decimals ?? 2}
                  onChange={e => updateColumn(i, { decimals: parseInt(e.target.value) })}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value={0}>0 (whole numbers)</option>
                  <option value={2}>2 (standard)</option>
                  <option value={4}>4 (precise)</option>
                </select>
              </div>
            </div>
          )}
          {col.type === 'dropdown' && (
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Options (comma or newline separated)</label>
              <OptionsInput options={col.options} onChange={opts => updateColumn(i, { options: opts })} />
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={col.required || false}
              onChange={e => updateColumn(i, { required: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600" />
            <span className="text-xs text-gray-600">Required in each row</span>
          </label>
        </div>
      ))}
      {columns.length < 4 ? (
        <button onClick={addColumn}
          className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors">
          + Add Column
        </button>
      ) : (
        <p className="text-xs text-amber-600 text-center py-1">Maximum of 4 columns allowed</p>
      )}
      <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Min Rows</label>
          <input type="number" value={minRows ?? ''} min={0} max={50}
            onChange={e => onMinChange(e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="0"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Max Rows</label>
          <input type="number" value={maxRows ?? ''} min={1} max={100}
            onChange={e => onMaxChange(e.target.value ? parseInt(e.target.value) : undefined)}
            placeholder="No limit"
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        </div>
      </div>
    </div>
  );
}

const SUB_FIELD_TYPES = [
  { value: 'TEXT', label: 'Text' },
  { value: 'TEXTAREA', label: 'Text Area' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'CURRENCY', label: 'Currency' },
  { value: 'DATE', label: 'Date' },
  { value: 'DATETIME', label: 'Date & Time' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'MULTI_SELECT', label: 'Multi-Select' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'TOGGLE', label: 'Toggle' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'URL', label: 'URL' },
  { value: 'RECORD_LINK', label: 'Record Link' },
];

function RepeaterSectionEditor({ subFields, onChange, sectionLabel, onSectionLabelChange, maxRows, onMaxRowsChange, allRecordTypes, recordTypeId }) {
  const addSubField = () => {
    onChange([...subFields, { key: '', label: '', type: 'TEXT', required: false, order: subFields.length + 1 }]);
  };
  const updateSubField = (index, updates) => {
    const updated = [...subFields];
    updated[index] = { ...updated[index], ...updates };
    if (updates.label) {
      updated[index].key = updates.label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/(^_|_$)/g, '');
    }
    onChange(updated);
  };
  const removeSubField = (index) => {
    onChange(subFields.filter((_, i) => i !== index));
  };
  const moveSubField = (index, dir) => {
    if ((dir === -1 && index === 0) || (dir === 1 && index === subFields.length - 1)) return;
    const updated = [...subFields];
    const swapIdx = index + dir;
    [updated[index], updated[swapIdx]] = [updated[swapIdx], updated[index]];
    updated.forEach((sf, i) => { sf.order = i + 1; });
    onChange(updated);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Section Label</label>
        <input type="text" value={sectionLabel}
          onChange={e => onSectionLabelChange(e.target.value)}
          placeholder="e.g., Medical Bills"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
        <p className="text-xs text-gray-400 mt-1">Shown as the collapsible section header</p>
      </div>
      <label className="block text-sm font-medium text-gray-700">Sub-Fields</label>
      {subFields.length === 0 && (
        <p className="text-xs text-gray-400">No sub-fields defined yet. Add sub-fields to define the row structure.</p>
      )}
      {subFields.map((sf, i) => (
        <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-gray-500">Sub-Field {i + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => moveSubField(i, -1)} disabled={i === 0}
                className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30 px-1">&#9650;</button>
              <button onClick={() => moveSubField(i, 1)} disabled={i === subFields.length - 1}
                className="text-xs text-gray-400 hover:text-gray-600 disabled:opacity-30 px-1">&#9660;</button>
              <button onClick={() => removeSubField(i)} className="text-xs text-red-500 hover:text-red-700 ml-2">Remove</button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Label</label>
              <input type="text" value={sf.label}
                onChange={e => updateSubField(i, { label: e.target.value })}
                placeholder="e.g., Provider Name"
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Type</label>
              <select value={sf.type}
                onChange={e => updateSubField(i, { type: e.target.value })}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                {SUB_FIELD_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Key</label>
            <input type="text" value={sf.key}
              onChange={e => updateSubField(i, { key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              placeholder="auto-generated from label"
              className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-1 focus:ring-brand-500" />
          </div>
          {sf.type === 'DROPDOWN' && (
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Options (comma or newline separated)</label>
              <OptionsInput options={sf.options} onChange={opts => updateSubField(i, { options: opts })} />
            </div>
          )}
          {sf.type === 'MULTI_SELECT' && (
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Options (comma or newline separated)</label>
              <OptionsInput options={sf.options} onChange={opts => updateSubField(i, { options: opts })} />
            </div>
          )}
          {sf.type === 'CURRENCY' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-0.5">Symbol</label>
                <select value={sf.currency_symbol || '$'}
                  onChange={e => updateSubField(i, { currency_symbol: e.target.value })}
                  className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                  <option value="$">$ (USD)</option>
                  <option value="€">€ (EUR)</option>
                  <option value="£">£ (GBP)</option>
                  <option value="¥">¥ (JPY/CNY)</option>
                </select>
              </div>
            </div>
          )}
          {sf.type === 'RECORD_LINK' && (
            <div>
              <label className="block text-xs text-gray-500 mb-0.5">Target Record Type</label>
              <select value={sf.target_record_type_id || ''}
                onChange={e => {
                  const rt = allRecordTypes.find(r => r.id === e.target.value);
                  updateSubField(i, { target_record_type_id: e.target.value, target_type_name: rt?.name || '' });
                }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500">
                <option value="" disabled>— Select a record type —</option>
                {allRecordTypes.filter(rt => rt.id !== recordTypeId).map(rt => (
                  <option key={rt.id} value={rt.id}>{rt.name}</option>
                ))}
              </select>
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={sf.required || false}
              onChange={e => updateSubField(i, { required: e.target.checked })}
              className="h-3.5 w-3.5 rounded border-gray-300 text-brand-600" />
            <span className="text-xs text-gray-600">Required in each row</span>
          </label>
        </div>
      ))}
      <button onClick={addSubField}
        className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-xs text-gray-500 hover:border-brand-400 hover:text-brand-600 transition-colors">
        + Add Sub-Field
      </button>
      <div className="pt-2 border-t border-gray-200">
        <label className="block text-xs text-gray-500 mb-0.5">Max Rows (hard cap)</label>
        <input type="number" value={maxRows ?? 10} min={1} max={10}
          onChange={e => onMaxRowsChange(e.target.value ? Math.min(parseInt(e.target.value), 10) : 10)}
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500" />
        <p className="text-xs text-gray-400 mt-1">Maximum 10 rows per section</p>
      </div>
    </div>
  );
}
